import React, {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState
} from 'react';
import JoditEditor from 'jodit-react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col, Drawer, Row } from 'antd';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { pageBreakBeforeButton } from './CustomComponents/pageBreakBeforeButton';
import { pageBreakAfterButton } from './CustomComponents/pageBreakAfterButton';
import { tableBordersButton } from './CustomComponents/tableBordersButton';
import { templateKeysButton } from './CustomComponents/templateKeysButton';
import { paragraphSpacingButtons } from './CustomComponents/paragraphSpacingButtons';
import { TemplateKeysPanel } from './TemplateKeysPanel';
import { createConfig } from './config';
import { EDITOR_FRAME_WIDTH_PX } from './printableWidth';
import {
  bindOperatorConditionSync,
  insertOperatorCondition,
  insertTemplateKey,
  saveEditorSelection
} from './utils/insertTemplateKey';
import { syncTemplateKeysStyle } from './utils/syncTemplateKeysStyle';
import { normalizeConditionMarkers } from './utils/normalizeConditionMarkers';
import { bindTemplateKeyInteractions } from './utils/templateKeyInteractions';

// Below this width the keys panel cannot sit next to the editor: it opens in
// a drawer instead.
const KEYS_PANEL_MIN_WIDTH_PX = 280;
const SIDE_BY_SIDE_MIN_WIDTH_PX =
  EDITOR_FRAME_WIDTH_PX + KEYS_PANEL_MIN_WIDTH_PX + 16;

/**
 * TemplateEditor component to render a rich text editor with the template
 * keys panel docked next to it.
 *
 * @component
 *
 * @param {Object} props - The props for the component.
 * @param {Array} props.tags - The tag tree fetched for the picked use case.
 * @param {Object} props.content - The header, body and footer of the template.
 * @param {Function} props.setContent - The function to update the content state.
 * @param {string} props.objective - Objective of the editor (either one of header, body or footer).
 * @param {boolean} props.isKeysPanelOpen - Whether the keys panel is shown.
 * @param {Function} props.onToggleKeysPanel - Shows or hides the keys panel.
 * @returns {JSX.Element} The TemplateEditor component.
 */
const TemplateEditor = ({
  tags,
  content,
  setContent,
  objective,
  isKeysPanelOpen,
  onToggleKeysPanel
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  // The HTML source view is a developer tool.
  const canEditSource = user?.role === 'developers:DEVELOPER';
  const editorInstanceRef = useRef(null);
  // The toolbar control is built once with the config; going through a ref
  // lets it always call the latest toggle handler.
  const rowRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setIsNarrow(entry.contentRect.width < SIDE_BY_SIDE_MIN_WIDTH_PX)
    );
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  // On a narrow screen the toolbar control opens the drawer; otherwise it
  // shows or hides the docked panel (a remembered preference).
  const handleToggleKeys = isNarrow
    ? () => setIsDrawerOpen((open) => !open)
    : onToggleKeysPanel;
  const toggleRef = useRef(handleToggleKeys);
  toggleRef.current = handleToggleKeys;

  const actualContent = content[objective];

  // jodit-react destroys and rebuilds the editor whenever the config identity
  // changes, so it is built once and never depends on the tags: the panel
  // consumes them on the React side.
  const config = useMemo(
    () =>
      createConfig(
        [pageBreakBeforeButton(), pageBreakAfterButton(), tableBordersButton()],
        [templateKeysButton(t, () => toggleRef.current())],
        { canEditSource, paragraphSpacingButtons: paragraphSpacingButtons(t) }
      ),
    [t, canEditSource]
  );

  // Receives the Jodit instance; the `ref` prop would only give the hidden
  // textarea.
  const handleEditorReady = useCallback((jodit) => {
    editorInstanceRef.current = jodit;
    // Jodit skips the non-editable keys when it styles a selection; the
    // hook gives them the same wrapper as the text around them.
    jodit.e.on('afterCommitStyle', (style) =>
      syncTemplateKeysStyle(jodit, style)
    );
    jodit.waitForReady().then(() => {
      bindOperatorConditionSync(jodit);
      bindTemplateKeyInteractions(jodit);
    });
  }, []);

  const handleChange = useCallback(
    (value) =>
      setContent((prev) => {
        const updatedState = { ...prev };
        updatedState[objective] = value;
        return updatedState;
      }),
    [setContent, objective]
  );

  const handleInsertKey = (value, type, selectedLabel) =>
    insertTemplateKey(editorInstanceRef.current, value, type, t, selectedLabel);

  const handleInsertOperator = (symbol, label) =>
    insertOperatorCondition(editorInstanceRef.current, symbol, label);

  // The panel search box takes the focus away from the editor; bookmarking
  // the caret first keeps the next insertion where the author left it.
  const handleSaveSelection = useCallback(
    () => saveEditorSelection(editorInstanceRef.current),
    []
  );

  const changePowered = () => {
    const container = document.getElementsByClassName(
      'jodit-status-bar__item jodit-status-bar__item-right'
    )[0];
    const newPowered =
      "<a tabindex='1' style='text-transform: uppercase' class='jodit-status-bar-link' target='_blank' href='https://strateg.in'>Powered by Strateg.in</a>";
    const oldPowered = document.getElementsByClassName(
      'jodit-status-bar__item jodit-status-bar__item-right'
    )[2];
    oldPowered.remove();
    container.innerHTML = newPowered;
  };

  useEffect(() => {
    changePowered();
  }, []);

  // Frozen on purpose: feeding the live value back on every keystroke would
  // make jodit-react reset the editor content and lose the caret.
  const editor = useMemo(
    () => (
      <JoditEditor
        editorRef={handleEditorReady}
        value={normalizeConditionMarkers(actualContent)}
        config={config}
        onChange={handleChange}
      />
    ),
    [objective, config, handleChange, handleEditorReady]
  );

  const keysPanel = (onClose) => (
    <TemplateKeysPanel
      tags={tags}
      onInsertKey={handleInsertKey}
      onInsertOperator={handleInsertOperator}
      onSaveSelection={handleSaveSelection}
      onClose={onClose}
    />
  );

  return (
    <>
      <Row ref={rowRef} gutter={[16, 16]} justify="center">
        <Col
          flex={`0 1 ${EDITOR_FRAME_WIDTH_PX}px`}
          style={{ maxWidth: '100%' }}
        >
          {editor}
        </Col>
        {!isNarrow && isKeysPanelOpen && (
          <Col flex="1 1 280px" style={{ maxWidth: 420 }}>
            {keysPanel(onToggleKeysPanel)}
          </Col>
        )}
      </Row>
      {isNarrow && (
        <Drawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          placement="right"
          width={380}
          mask={false}
          autoFocus={false}
          closable={false}
          rootClassName="template-keys-drawer"
          styles={{ body: { padding: 0 } }}
        >
          {keysPanel(() => setIsDrawerOpen(false))}
        </Drawer>
      )}
    </>
  );
};

TemplateEditor.propTypes = {
  tags: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, type: PropTypes.string })
  ),
  content: PropTypes.shape({
    header: PropTypes.string,
    body: PropTypes.string,
    footer: PropTypes.string
  }).isRequired,
  setContent: PropTypes.func.isRequired,
  objective: PropTypes.oneOf(['header', 'body', 'footer']).isRequired,
  isKeysPanelOpen: PropTypes.bool,
  onToggleKeysPanel: PropTypes.func.isRequired
};

TemplateEditor.defaultProps = {
  tags: [],
  isKeysPanelOpen: true
};

export default TemplateEditor;
